/**
 * Helpers de formatage pour les données d'auth (utilisateur + domaine).
 *
 * Centralisés ici pour éviter la duplication entre Topbar, Sidebar et
 * Dashboard. Les libellés sont en français — affichés tels quels dans l'UI.
 */

/**
 * Initiales d'un utilisateur sur 2 lettres maximum.
 *
 *   ("Marc", "Lefèvre")  → "ML"
 *   ("Anne", "")         → "A"
 *   ("", "")             → "?"
 *
 * Robuste aux espaces, accents et caractères composés (Unicode-aware
 * avec `[...str]` qui itère par code-point, pas par UTF-16 unit).
 */
export function initiales(prenom: string, nom: string): string {
  const p = [...prenom.trim()][0]?.toUpperCase() ?? "";
  const n = [...nom.trim()][0]?.toUpperCase() ?? "";
  const result = `${p}${n}`;
  return result || "?";
}

/** Nom complet "Prénom Nom" en strippant les espaces parasites. */
export function nomComplet(prenom: string, nom: string): string {
  return `${prenom.trim()} ${nom.trim()}`.trim() || "Utilisateur";
}

/**
 * Libellé d'affichage du statut professionnel CIVC.
 * Source : docs/reglementation/etiquetage-aoc-champagne.md
 *
 * Le code (RM/NM/CM/RC/SR) reste réglementaire et apparaît sur l'étiquette.
 * Ce label est uniquement pour l'UI (header dashboard, paramètres).
 */
const STATUTS_PROFESSIONNELS: Record<string, string> = {
  RM: "Récoltant-Manipulant",
  NM: "Négociant-Manipulant",
  CM: "Coopérative de Manipulation",
  RC: "Récoltant-Coopérateur",
  SR: "Société de Récoltants",
  MA: "Marque d'Acheteur",
  ND: "Négociant-Distributeur",
};

export function statutProfessionnelLabel(code: string | null | undefined): string {
  if (!code) return "Statut à compléter";
  return STATUTS_PROFESSIONNELS[code] ?? code;
}

/**
 * Libellé d'un rôle RBAC pour affichage UI.
 * Codes seedés à la création du domaine (cf packages/database/src/schema/roles.ts).
 */
const ROLES: Record<string, string> = {
  proprietaire: "Propriétaire",
  chef_de_cave: "Chef de cave",
  comptable: "Comptable",
  commercial: "Commercial",
  salarie_vigne: "Salarié vigne",
  lecture_seule: "Lecture seule",
  oenologue_conseil_externe: "Œnologue conseil",
};

export function roleLabel(code: string | null | undefined): string {
  if (!code) return "Membre";
  return ROLES[code] ?? code;
}
